import { AxiosResponse } from "axios";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  ICoupon,
  ICouponCode,
  ICouponPayload,
  ICouponQuery,
  ICouponsQuery,
  ICreateCoupon,
  ICreateCouponResponse,
  IDeleteCouponAction,
  IUpdateCoupon,
  IUpdateCouponResponse,
} from "./coupon.types";
import { IPaginatedResponse } from "../../../types";
import qs from "qs";
import { filterNonNull } from "../../../utils";

class CouponService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Creates a new coupon.
   *
   * @param payload - Coupon details including code, discount, validity, and limits.
   * @returns The created coupon object with `_id` and timestamps.
   *
   * @example
   * const { data } = await zott.coupons.createV2({
   *   coupon_code: "SAVE10",
   *   name: "10% Off",
   *   description: "Save 10% on any plan",
   *   discount: 10,
   *   type: "percentage",
   *   validity: { start_date: "2025-01-01", end_date: "2025-12-31" },
   *   max_users: 100,
   * });
   * console.log(data.data._id);
   */
  async createV2(
    payload: ICouponPayload,
  ): Promise<AxiosResponse<ICreateCouponResponse>> {
    return this.request({
      method: "POST",
      url: "/api/v2/coupons",
      data: payload,
    });
  }

  /**
   * Updates an existing coupon by ID.
   *
   * @param id - The coupon `_id` to update.
   * @param payload - Fields to update (same shape as `createV2`).
   * @returns A message and the updated coupon `id`.
   *
   * @example
   * const { data } = await zott.coupons.updateV2("coupon_id", {
   *   coupon_code: "SAVE20",
   *   discount: 20,
   *   // ...other fields
   * });
   */
  async updateV2(
    id: string,
    payload: ICouponPayload,
  ): Promise<AxiosResponse<IUpdateCouponResponse>> {
    return this.request({
      method: "PUT",
      url: `/api/v2/coupons/${id}`,
      data: payload,
    });
  }

  /**
   * Deletes a coupon by ID.
   *
   * @param id - The coupon `_id` to delete.
   * @returns A confirmation message.
   *
   * @example
   * await zott.coupons.deleteV2("coupon_id");
   */
  async deleteV2(id: string): Promise<AxiosResponse<{ message: string }>> {
    return this.request({
      method: "DELETE",
      url: `/api/v2/coupons/${id}`,
    });
  }

  /**
   * Retrieves a paginated list of coupons for management.
   *
   * @param query - Optional filters: `page`, `limit`, `order`, `q` (search), `status`, `fields`, `populate`, `filters`.
   * @returns A paginated list of `ICouponCode` objects.
   *
   * @example
   * const { data } = await zott.coupons.getManageV2({ page: 1, limit: 20, status: "active" });
   * console.log(data.docs); // ICouponCode[]
   */
  async getManageV2(
    query?: ICouponsQuery,
  ): Promise<AxiosResponse<IPaginatedResponse<ICouponCode>>> {
    return this.request({
      method: "GET",
      url: `/api/v2/coupons/manage?${qs.stringify(filterNonNull(query || {}))}`,
    });
  }

  // ─── Legacy (v1) methods ─────────────────────────────────────────────────────

  /**
   * @deprecated Use {@link createV2} instead. Will be removed in a future release.
   *
   * Creates a new coupon via the v1 API.
   *
   * @param payload - Legacy coupon payload.
   * @returns The `id` of the created coupon.
   */
  async create(payload: ICreateCoupon): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "POST",
      url: "/api/v1/coupons",
      data: payload,
    });
  }

  /**
   * @deprecated Use {@link updateV2} instead. Will be removed in a future release.
   *
   * Updates an existing coupon via the v1 API.
   *
   * @param id - The coupon ID to update.
   * @param payload - Legacy coupon payload.
   * @returns The `id` of the updated coupon.
   */
  async update(
    id: string,
    payload: IUpdateCoupon,
  ): Promise<AxiosResponse<{ id: string }>> {
    return this.request({
      method: "PUT",
      url: `/api/v1/coupons/${id}`,
      data: payload,
    });
  }

  /**
   * @deprecated Use {@link deleteV2} instead. Will be removed in a future release.
   *
   * Deletes coupons by an array of IDs via the v1 API.
   *
   * @param id - Array of coupon IDs to delete.
   * @returns The count of deleted coupons.
   */
  async delete(id: string[]): Promise<AxiosResponse<{ deletedCount: number }>> {
    return this.request({
      method: "DELETE",
      url: "/api/v1/coupons",
      data: { id },
    });
  }

  /**
   * @deprecated Use {@link deleteV2} or status-based updates instead. Will be removed in a future release.
   *
   * Performs a bulk action (e.g. activate/deactivate) on multiple coupons.
   *
   * @param payload - Action (`"active"` | `"inactive"`) and an array of coupon IDs.
   * @returns The count of updated coupons.
   */
  async action(
    payload: IDeleteCouponAction,
  ): Promise<AxiosResponse<{ updatedCount: number }>> {
    return this.request({
      method: "PATCH",
      url: "/api/v1/coupons/action",
      data: payload,
    });
  }

  /**
   * @deprecated Use {@link getManageV2} instead. Will be removed in a future release.
   *
   * Retrieves a paginated list of coupons via the v1 API.
   *
   * @param query - Optional filters: `page`, `limit`, `order`, `sortBy`, `q`, `slug`, `select`, `filters`.
   * @returns A paginated list of legacy `ICoupon` objects.
   */
  async getManage(
    query?: ICouponQuery,
  ): Promise<AxiosResponse<IPaginatedResponse<ICoupon>>> {
    return this.request({
      method: "GET",
      url: `/api/v1/coupons/manage?${qs.stringify(filterNonNull(query || {}))}`,
    });
  }
}

export default CouponService;
